import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";

interface TooltipProps {
  content?: string;
  anchorSelector: string; // selector to anchor tooltip to
  className?: string;
  children?: React.ReactNode;
  debug?: boolean;
  isRTL?: boolean;
  placement?: "top" | "bottom" | "left" | "right";
}

type Position = {
  top: number;
  left: number;
  visible: boolean;
  placement?: "top" | "bottom" | "left" | "right";
  arrowLeft?: number;
  arrowTop?: number;
};

export const Tooltip = ({
  content,
  anchorSelector,
  className = "",
  children,
  debug = false,
  isRTL = false,
  placement = "bottom",
}: TooltipProps) => {
  const [position, setPosition] = useState<Position>({
    top: 0,
    left: 0,
    visible: false,
  });
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [tooltipDimensions, setTooltipDimensions] = useState({
    width: 0,
    height: 0,
  });

  const calculatePosition = useCallback(
    (
      anchorRect: DOMRect,
      tooltipWidth: number,
      tooltipHeight: number,
      finalCalc: boolean = false,
    ) => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      const tooltipMargin = 4;
      const containerPadding = 10;
      let top = 0;
      let left =
        anchorRect.left + scrollX + anchorRect.width / 2 - tooltipWidth / 2 + 2;

      if (placement === "top") {
        top = anchorRect.top + scrollY - tooltipHeight - tooltipMargin + 10;
      } else {
        top = anchorRect.bottom + scrollY + tooltipMargin - 10;
      }

      left = Math.max(
        containerPadding,
        Math.min(
          left,
          viewportWidth + scrollX - tooltipWidth - containerPadding,
        ),
      );
      top = Math.max(
        containerPadding,
        Math.min(
          top,
          viewportHeight + scrollY - tooltipHeight - containerPadding,
        ),
      );

      let arrowLeft = 42;
      let arrowTop = -8;

      if (finalCalc) {
        arrowLeft = anchorRect.left + scrollX + anchorRect.width / 2 - left;
        arrowLeft = Math.max(10, Math.min(arrowLeft, tooltipWidth - 20));
      }

      return { top, left, placement, arrowLeft, arrowTop };
    },
    [placement],
  );

  useEffect(() => {
    if (position.visible && tooltipRef.current) {
      const rect = tooltipRef.current.getBoundingClientRect();
      if (
        rect.width !== tooltipDimensions.width ||
        rect.height !== tooltipDimensions.height
      ) {
        setTooltipDimensions({ width: rect.width, height: rect.height });
      }
    }
  }, [position.visible, tooltipDimensions]);

  useEffect(() => {
    const anchor = document.querySelector(anchorSelector);

    if (!anchor) return;

    const show = () => {
      const rect = anchor.getBoundingClientRect();

      const estimatedWidth = 100;
      const estimatedHeight = 40;

      const { top, left, placement, arrowLeft, arrowTop } = calculatePosition(
        rect,
        estimatedWidth,
        estimatedHeight,
        false,
      );

      setPosition({
        top,
        left,
        visible: true,
        placement,
        arrowLeft,
        arrowTop,
      });
    };

    const hide = () => {
      setPosition((prev) => ({ ...prev, visible: false }));
    };

    const updatePosition = () => {
      if (position.visible && tooltipRef.current) {
        const anchor = document.querySelector(anchorSelector);
        if (anchor) {
          const anchorRect = anchor.getBoundingClientRect();
          const tooltipRect = tooltipRef.current.getBoundingClientRect();
          const { top, left, placement, arrowLeft, arrowTop } =
            calculatePosition(
              anchorRect,
              tooltipRect.width,
              tooltipRect.height,
              true,
            );
          setPosition((prev) => ({
            ...prev,
            top,
            left,
            placement,
            arrowLeft,
            arrowTop,
          }));
        }
      }
    };

    anchor.addEventListener("mouseenter", show);
    anchor.addEventListener("mouseleave", hide);
    window.addEventListener("scroll", updatePosition);
    window.addEventListener("resize", updatePosition);

    return () => {
      anchor.removeEventListener("mouseenter", show);
      anchor.removeEventListener("mouseleave", hide);
      window.removeEventListener("scroll", updatePosition);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorSelector, calculatePosition, position.visible]);

  useEffect(() => {
    if (
      position.visible &&
      tooltipRef.current &&
      tooltipDimensions.width > 0 &&
      tooltipDimensions.height > 0
    ) {
      const anchor = document.querySelector(anchorSelector);
      if (anchor) {
        const anchorRect = anchor.getBoundingClientRect();
        const { top, left, placement, arrowLeft, arrowTop } = calculatePosition(
          anchorRect,
          tooltipDimensions.width,
          tooltipDimensions.height,
          true,
        );
        setPosition((prev) => ({
          ...prev,
          top,
          left,
          placement,
          arrowLeft,
          arrowTop,
        }));
      }
    }
  }, [tooltipDimensions, anchorSelector, calculatePosition, position.visible]);

  const transformClass = "transform -translate-x-1/2";
  const portalTarget =
    document.querySelector(
      ".cbb-full-screen-preview-wrapper, .cbb-embed-preview-wrapper",
    ) || document.body;

  return createPortal(
    <AnimatePresence>
      {(position.visible || debug) && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { delay: 0, duration: 0.15 } }}
          transition={{ duration: 0.15, delay: 0.3 }}
          ref={tooltipRef}
          className={`cbb-common-tooltip sm-tooltip pointer-events-none fixed ${transformClass} ${className} tooltip-${position.placement || placement}`}
          style={
            {
              top: `${position.top}px`,
              left: `${position.left}px`,
              "--arrow-left": `${position.arrowLeft}px`,
              "--arrow-top": `${position.arrowTop}px`,
              zIndex: 10000000,
              pointerEvents: "none",
            } as React.CSSProperties
          }
          dir={isRTL ? "rtl" : "ltr"}
        >
          <span className="sm-tooltip_text">{children || content}</span>
        </motion.p>
      )}
    </AnimatePresence>,
    portalTarget as Element,
  );
};
